import React, { useState } from 'react';

interface CustomDomainModalProps {
    isOpen: boolean;
    toggle: () => void;
    handleForm: any;
}

const CustomDomainModal: React.FC<CustomDomainModalProps> = ({ isOpen, toggle, handleForm }) => {
    const [customDomain, setCustomDomain] = useState(handleForm.values.customDomain || '');
    const [error, setError] = useState('');

    const mainDomain = handleForm.values.domain;

    const validateDomain = (domain: string) => {
        if (!domain) {
            setError('');
            return true;
        }

        // Check if it's a valid hostname format
        const hostnameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!hostnameRegex.test(domain)) {
            setError('Formato de domínio inválido. Use apenas letras, números, pontos e hífens.');
            return false;
        }

        // Check if it's a subdomain of the main domain
        if (!domain.endsWith('.' + mainDomain)) {
            setError(`O domínio customizado deve ser um subdomínio de ${mainDomain}`);
            return false;
        }

        setError('');
        return true;
    };

    const handleSave = () => {
        if (validateDomain(customDomain)) {
            handleForm.setFieldValue('customDomain', customDomain);
            toggle();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCustomDomain(value);
        validateDomain(value);
    };

    const generateSuggestedDomain = () => {
        return `tag.${mainDomain}`;
    };

    const handleUseSuggested = () => {
        const suggested = generateSuggestedDomain();
        setCustomDomain(suggested);
        validateDomain(suggested);
    };

    return (
        <div className={`modal ${isOpen ? 'show d-block' : ''}`} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title"><span className="text-primary">🌐</span> Configurar Domínio Customizado</h5>
                        <button type="button" className="btn-close" onClick={toggle} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="alert alert-info mb-4">
                            <h6 className="alert-heading">
                                <i className="fas fa-info-circle me-2"></i>
                                Como configurar seu domínio customizado:
                            </h6>
                            <ol className="mb-0 ps-3">
                                <li>Escolha um subdomínio do seu domínio principal: <strong>{mainDomain}</strong></li>
                                <li>Crie um registro CNAME no seu provedor DNS apontando para: <code className="text-dark">tag.adpulse.com.br</code></li>
                                <li>Mantenha o Cloudflare ou qualquer CDN <strong>desativado</strong> para este subdomínio</li>
                                <li>Aguarde a propagação DNS (pode levar até 24 horas)</li>
                            </ol>
                        </div>

                        <div className="mb-3">
                            <div className="form-group">
                                <label htmlFor="customDomain" className="form-label">
                                    Domínio Customizado
                                    <small className="text-muted ms-2">(opcional)</small>
                                </label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className={`form-control ${error ? 'is-invalid' : ''}`}
                                        id="customDomain"
                                        value={customDomain}
                                        onChange={handleInputChange}
                                        placeholder={`Ex: ${generateSuggestedDomain()}`}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleUseSuggested}
                                        title="Usar sugestão"
                                    >
                                        Sugerir
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div className="invalid-feedback d-block">
                                    {error}
                                </div>
                            )}
                            {!error && customDomain && (
                                <small className="text-success">
                                    ✓ Domínio válido
                                </small>
                            )}
                        </div>

                        <div className="alert alert-warning mt-3">
                            <h6 className="alert-heading">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Configuração DNS Necessária:
                            </h6>
                            <div className="row">
                                <div className="col-md-4"><strong>Tipo:</strong></div>
                                <div className="col-md-8"><code>CNAME</code></div>
                            </div>
                            <div className="row">
                                <div className="col-md-4"><strong>Nome:</strong></div>
                                <div className="col-md-8"><code>{customDomain || `tag`}</code></div>
                            </div>
                            <div className="row">
                                <div className="col-md-4"><strong>Valor:</strong></div>
                                <div className="col-md-8"><code>tag.adpulse.com.br</code></div>
                            </div>
                            <div className="row">
                                <div className="col-md-4"><strong>TTL:</strong></div>
                                <div className="col-md-8"><code>300</code> (5 minutos)</div>
                            </div>
                        </div>

                        <div className="alert alert-light mt-3">
                            <h6 className="alert-heading">
                                <i className="fas fa-lightbulb me-2"></i>
                                Benefícios do Domínio Customizado:
                            </h6>
                            <ul className="mb-0">
                                <li>Coleta de dados first-party</li>
                                <li>Maior compatibilidade com bloqueadores de anúncio</li>
                                <li>Melhor controle sobre privacidade e dados</li>
                                <li>Aumento potencial de receita</li>
                            </ul>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={toggle}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={!!error}
                        >
                            Salvar Configuração
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomDomainModal;